// Import datastore helper functions if needed from '@freelancer/datastore/core'
// import { toNumber } from '@freelancer/datastore/core';
import { _transformBid } from '@freelancer/datastore/bids';
import { _transformCurrency } from '@freelancer/datastore/currencies';
import { transformJob } from '@freelancer/datastore/jobs';
import { transformLocation } from '@freelancer/datastore/project-view-users';
import {
  _emptyProjectUpgrades,
  _transformBidStats,
  _transformProjectBudget,
  _transformProjectHourlyProjectInfo,
  _transformProjectUpgrades,
} from '@freelancer/datastore/projects';
import { QualificationApi } from 'api-typings/common/common';
import {
  NDASignatureApi,
  ProjectApi,
  ProjectAttachmentApi,
  ProjectCollaborationApi,
  ProjectsGetResultApi,
} from 'api-typings/projects/projects';
import {
  NDASignature,
  ProjectAttachment,
  ProjectCollaboration,
  ProjectViewProject,
  Qualification,
} from './project-view-projects.model';

// Transforms what the backend returns into a format your components can consume
// Should only be called in this collection's reducer
export function transformProjectViewProjects(
  project: ProjectApi,
  allSelectedBids: ProjectsGetResultApi['selected_bids'],
): ProjectViewProject {
  if (
    !project.bid_stats ||
    !project.budget ||
    !project.currency ||
    !project.description ||
    !project.location ||
    !project.seo_url ||
    !project.time_submitted ||
    !project.type ||
    !project.submitdate
  ) {
    throw new ReferenceError(`Missing a required project field.`);
  }

  const selectedBids = allSelectedBids ? allSelectedBids[project.id] : [];

  return {
    id: project.id,

    // Extra things for freelancer pvp
    attachments: (project.attachments || []).map(attachment =>
      transformProjectAttachment(attachment),
    ),
    description: project.description,
    location: transformLocation(project.location),
    qualifications: project.qualifications
      ? project.qualifications.map(transformQualification)
      : [],
    jobs: (project.jobs || []).map(transformJob),

    // Standard project things. We should reused the other logic eventually.
    bidPeriod: project.bidperiod,
    bidStats: _transformBidStats(project.bid_stats),
    budget: _transformProjectBudget(project.budget),
    currency: _transformCurrency(project.currency),
    deleted: project.deleted,
    frontendProjectStatus: project.frontend_project_status,
    hideBids: project.hidebids,
    hireme: project.hireme || false,
    hourlyProjectInfo: project.hourly_project_info
      ? _transformProjectHourlyProjectInfo(project.hourly_project_info)
      : undefined,
    language: project.language || 'en',
    local: project.local || false,
    ndaSignatures: project.nda_signatures
      ? project.nda_signatures.map(transformNDASignatures)
      : [],
    ownerId: project.owner_id,
    previewDescription: project.preview_description || '',
    projectCollaborations: project.project_collaborations
      ? project.project_collaborations.map(transFormProjectCollaboration)
      : [],
    seoUrl: project.seo_url,
    status: project.status,
    submitDate: project.submitdate,
    subStatus: project.sub_status,
    selectedBids: selectedBids ? selectedBids.map(_transformBid) : [],
    timeSubmitted: project.time_submitted * 1000, // Turn seconds to ms
    title: project.title,
    type: project.type,
    upgrades: project.upgrades
      ? _transformProjectUpgrades(project.upgrades)
      : _emptyProjectUpgrades(),
    canPostReview: project.can_post_review || {},
  };
}

function transformProjectAttachment(
  attachment: ProjectAttachmentApi,
): ProjectAttachment {
  return {
    id: attachment.id,
    filename: attachment.filename,
    timeSubmitted: (attachment.time_submitted || 0) * 1000, // Turn seconds to ms,
    url: `https://${attachment.url}`,
    accessHash: attachment.access_hash,
    contentType: attachment.content_type,
  };
}

export function transformQualification(
  qualification: QualificationApi,
): Qualification {
  return {
    id: qualification.id,
    description: qualification.description,
    iconName: qualification.icon_name,
    iconUrl: qualification.icon_url,
    level: qualification.level,
    name: qualification.name,
    scorePercentage: qualification.score_percentage,
    type: qualification.type,
    userPercentile: qualification.user_percentile,
  };
}

function transformNDASignatures(signatures: NDASignatureApi): NDASignature {
  return {
    timeSigned: signatures.time_signed
      ? signatures.time_signed * 1000
      : undefined,
    projectId: signatures.project_id,
    userId: signatures.user_id,
  };
}

function transFormProjectCollaboration(
  projectCollaboration: ProjectCollaborationApi,
): ProjectCollaboration {
  if (!projectCollaboration.id) {
    throw new ReferenceError(
      `Project Collaboration object does not have an id.`,
    );
  }

  if (!projectCollaboration.status) {
    throw new ReferenceError(
      `Project Collaboration object does not have status.`,
    );
  }
  return {
    context: projectCollaboration.context,
    contextOwnerId: projectCollaboration.context_owner_id,
    id: projectCollaboration.id,
    inviteToken: projectCollaboration.invite_token,
    permissions: projectCollaboration.permissions,
    status: projectCollaboration.status,
    timeCreated: projectCollaboration.time_created
      ? projectCollaboration.time_created * 1000
      : undefined,
    timeUpdated: projectCollaboration.time_updated
      ? projectCollaboration.time_updated * 1000
      : undefined,
    userId: projectCollaboration.user_id,
  };
}
